import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Typography,
    Box,
} from '@mui/material';
import useRequest from '../hooks/useRequest';

interface ContractHistoryModalProps {
    open: boolean;
    onClose: () => void;
    contractNr: number | null;
}

const ContractHistoryModal: React.FC<ContractHistoryModalProps> = ({
    open,
    onClose,
    contractNr,
}) => {
    const [history, setHistory] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const request = useRequest();

    useEffect(() => {
        const fetchHistory = async () => {
            if (!open || contractNr === null) return;

            setLoading(true);
            setError(null);
            try {
                const response = await request<{ history?: string[] }>({
                    path: `/contracts/${contractNr}/history`,
                    method: 'GET',
                });
                if (response?.history) {
                    setHistory(response.history);
                } else {
                    setHistory([]);
                }
            } catch (err: unknown) {
                console.error('Failed to fetch contract history:', err);
                setError('A apărut o eroare la încărcarea istoricului contractului.');
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [open, contractNr, request]);

    let content: React.ReactNode;
    if (loading) {
        content = (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    } else if (error) {
        content = <Typography color="error">{error}</Typography>;
    } else if (history.length > 0) {
        content = (
            <List dense>
                {history.map(line => (
                    <ListItem key={line}>
                        <ListItemText primary={line} />
                    </ListItem>
                ))}
            </List>
        );
    } else {
        content = <Typography>Nu există istoric pentru acest contract.</Typography>;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>ISTORIC CONTRACT</DialogTitle>
            <DialogContent dividers>{content}</DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="contained">
                    Închide
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContractHistoryModal;
